import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DependentView,
  InsuranceStatus,
  UpdateInsuranceInput,
  updateInsurance,
} from '../../../api/General/dependents';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle, DialogDescription,
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import { DateField } from '../../../components/dateFields';

interface UpdateInsuranceModalProps {
  dependent: DependentView;
  open: boolean;
  onClose: () => void;
  onUpdated: (updated: DependentView) => void;
}

const insuranceStatusOptions = [
  { value: InsuranceStatus.Pending, labelKey: 'insurancePending' },
  { value: InsuranceStatus.Active, labelKey: 'insuranceActive' },
  { value: InsuranceStatus.Inactive, labelKey: 'insuranceInactive' },
];

export default function UpdateInsuranceModal({ dependent, open, onClose, onUpdated }: UpdateInsuranceModalProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<InsuranceStatus>(dependent.insuranceStatus);
  const [provider, setProvider] = useState(dependent.insuranceProvider || '');
  const [policyNumber, setPolicyNumber] = useState(dependent.insurancePolicyNumber || '');
  const [expiryDate, setExpiryDate] = useState(dependent.insuranceExpiryDate ? dependent.insuranceExpiryDate.split('T')[0] : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setStatus(dependent.insuranceStatus);
    setProvider(dependent.insuranceProvider || '');
    setPolicyNumber(dependent.insurancePolicyNumber || '');
    setExpiryDate(dependent.insuranceExpiryDate ? dependent.insuranceExpiryDate.split('T')[0] : '');
    setError('');
  }, [dependent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const input: UpdateInsuranceInput = {
        id: dependent.id,
        insuranceStatus: status,
        insuranceProvider: provider || undefined,
        insurancePolicyNumber: policyNumber || undefined,
        insuranceExpiryDate: expiryDate || undefined,
      };

      const result = await updateInsurance(input);
      if (result.errors.length === 0) {
        onUpdated(result.payload);
        onClose();
      } else {
        setError(result.errors[0] || t('dependents.insurance.updateError'));
      }
    } catch {
      setError(t('dependents.insurance.updateError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="default">
        <DialogHeader>
          <DialogTitle>{t('dependents.insurance.updateTitle')}</DialogTitle>
          <DialogDescription>{dependent.fullName}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <form onSubmit={handleSubmit} className="space-y-4" id="update-insurance-form">
            {/* Current Status Display */}
            <div className="bg-muted rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{t('dependents.insurance.currentStatus')}</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                dependent.insuranceStatus === InsuranceStatus.Active ? 'bg-success/10 text-success' :
                dependent.insuranceStatus === InsuranceStatus.Pending ? 'bg-warning/10 text-warning' :
                'bg-destructive/10 text-destructive'
              }`}>
                {t(`dependents.insurance${insuranceStatusOptions.find(o => o.value === dependent.insuranceStatus)?.labelKey || 'Pending'}`)}
              </span>
            </div>

            {/* New Status */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('dependents.insurance.newStatus')}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as InsuranceStatus)}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                disabled={saving}
              >
                {insuranceStatusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(`dependents.insurance${opt.labelKey}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('dependents.provider')}
              </label>
              <input
                type="text"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                placeholder={t('dependents.insurance.providerPlaceholder')}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                disabled={saving}
              />
            </div>

            {/* Policy Number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('dependents.policyNumber')}
              </label>
              <input
                type="text"
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder={t('dependents.insurance.policyPlaceholder')}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                disabled={saving}
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('dependents.expiryDate')}
              </label>
              <DateField
                aria-label={t('dependents.expiryDate')}
                value={expiryDate}
                onChange={setExpiryDate}
                disabled={saving}
              />
            </div>

            {error && (
              <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </form>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="update-insurance-form" loading={saving}>
            {t('dependents.insurance.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
